/* ============================================================================
   ZARWANA — SHARED SHELL (Shopify theme build)
   ----------------------------------------------------------------------------
   Injects the header, footer, drawers and modals on every page and wires up
   cart / wishlist / search / quick-view. Catalog data comes from products.js;
   the bag is Shopify's own store cart via the AJAX Cart API, so the drawer,
   /cart and checkout are always the same bag.
   ========================================================================== */

(function () {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const page = document.body.dataset.page || "";
  const WHATSAPP_URL = "https://wa.me/919634782282";
  const ASSETS = window.THEME_ASSETS || {};

  /* Shopify's fixed theme routes */
  const URLS = {
    home: "/",
    catalog: "/collections/all",
    product: (handle) => `/products/${handle}`,
    cart: "/cart",
    checkout: "/checkout",
    account: "/account",
    about: "/pages/about",
    contact: "/pages/contact",
  };

  /* ------------------------------ icons --------------------------------- */
  const I = {
    search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.8-3.8"/></svg>',
    account: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="8" r="3.6"/><path d="M5 20c.8-3.6 3.6-5.4 7-5.4s6.2 1.8 7 5.4"/></svg>',
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5.5 8.5h13l-.9 11a1.8 1.8 0 0 1-1.8 1.6H8.2a1.8 1.8 0 0 1-1.8-1.6l-.9-11Z"/><path d="M8.8 8.2V7a3.2 3.2 0 0 1 6.4 0v1.2"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 20.4 4.9 13.6a4.6 4.6 0 0 1 0-6.5 4.4 4.4 0 0 1 6.3 0l.8.8.8-.8a4.4 4.4 0 0 1 6.3 0 4.6 4.6 0 0 1 0 6.5L12 20.4Z"/></svg>',
    heartFill: '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5"><path d="M12 20.4 4.9 13.6a4.6 4.6 0 0 1 0-6.5 4.4 4.4 0 0 1 6.3 0l.8.8.8-.8a4.4 4.4 0 0 1 6.3 0 4.6 4.6 0 0 1 0 6.5L12 20.4Z"/></svg>',
    burger: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16M4 12h16M4 17h16"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m6 6 12 12M18 6 6 18"/></svg>',
    chevL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m14.5 5.5-6.5 6.5 6.5 6.5"/></svg>',
    chevR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m9.5 5.5 6.5 6.5-6.5 6.5"/></svg>',
    play: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5v13l11-6.5-11-6.5Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.55"><rect x="5" y="5" width="14" height="14" rx="4"/><circle cx="12" cy="12" r="3.25"/><circle cx="16.5" cy="7.5" r=".8" fill="currentColor" stroke="none"/></svg>',
    facebook: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 20v-7h2.3l.4-3h-2.7V8.1c0-.9.3-1.5 1.6-1.5h1.3V4a18 18 0 0 0-2.4-.1c-2.4 0-4 1.5-4 4v2.1H7.4v3H10v7h3.5Z"/></svg>',
    youtube: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.1 7.2c-.2-.8-.8-1.4-1.6-1.6C17.1 5.2 12 5.2 12 5.2s-5.1 0-6.5.4c-.8.2-1.4.8-1.6 1.6-.4 1.4-.4 4.3-.4 4.3s0 2.9.4 4.3c.2.8.8 1.4 1.6 1.6 1.4.4 6.5.4 6.5.4s5.1 0 6.5-.4c.8-.2 1.4-.8 1.6-1.6.4-1.4.4-4.3.4-4.3s0-2.9-.4-4.3ZM10.3 14.2V8.8l4.7 2.7-4.7 2.7Z"/></svg>',
  };
  window.ZICONS = I;

  /* ------------------------------ stores --------------------------------- */
  const store = {
    get(key, fallback) {
      try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
    },
    set(key, val) { localStorage.setItem(key, JSON.stringify(val)); },
  };

  /* The bag IS the Shopify store cart (AJAX Cart API, same-origin). Every
     mutation round-trips through /cart/*.js and re-reads /cart.js, so the
     drawer, the /cart page and checkout can never drift apart. */
  const Cart = {
    cache: { lines: [], subtotal: 0, checkoutUrl: URLS.checkout },

    /* Storefront-API variant ids look like gid://shopify/ProductVariant/123 —
       the AJAX API wants the trailing numeric id. */
    _numericId(id) {
      const m = String(id).match(/(\d+)\s*$/);
      return m ? Number(m[1]) : Number(id);
    },

    _mapCart(cart) {
      return {
        lines: (cart.items || []).map((it) => ({
          lineId: it.key,
          variantId: it.id,
          productHandle: it.handle,
          name: it.product_title,
          variantTitle: !it.variant_title || it.variant_title === "Default Title" ? "" : it.variant_title,
          properties: it.properties || {},
          img: it.image || "",
          price: (it.final_price ?? it.price) / 100,
          qty: it.quantity,
        })),
        subtotal: (cart.items_subtotal_price || 0) / 100,
        checkoutUrl: URLS.checkout,
      };
    },

    async refresh() {
      try {
        const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
        if (res.ok) this.cache = this._mapCart(await res.json());
      } catch (err) {
        console.error("Zarwana: couldn't load cart —", err.message);
      }
      renderCartUI();
      window.dispatchEvent(new CustomEvent("zarwana:cart"));
    },

    items() { return this.cache.lines; },
    count() { return this.cache.lines.reduce((n, l) => n + l.qty, 0); },
    subtotal() { return this.cache.subtotal; },
    checkoutUrl() { return this.cache.checkoutUrl; },

    async add(variantId, qty = 1, properties = null) {
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            id: this._numericId(variantId),
            quantity: qty,
            /* line-item properties (e.g. { "Ring Size": "14" }) — Shopify
               carries these through checkout onto the order and its emails. */
            ...(properties ? { properties } : {}),
          }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          toast(j.description || j.message || "Couldn't add that to your bag.");
          return false;
        }
        await this.refresh();
        return true;
      } catch (err) {
        toast(err.message || "Couldn't add that to your bag.");
        return false;
      }
    },

    async setQty(lineId, qty) {
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: lineId, quantity: Math.max(0, qty) }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          toast(j.description || j.message || "Couldn't update your bag.");
        }
        await this.refresh();
      } catch (err) {
        toast(err.message || "Couldn't update your bag.");
      }
    },

    async remove(lineId) { await this.setQty(lineId, 0); },
  };
  const Wish = {
    KEY: "zarwana_wishlist",
    ids() { return store.get(this.KEY, []); },
    has(id) { return this.ids().includes(id); },
    toggle(id) {
      let ids = this.ids();
      const on = !ids.includes(id);
      ids = on ? [...ids, id] : ids.filter((x) => x !== id);
      store.set(this.KEY, ids);
      return on;
    },
  };
  const Recent = {
    KEY: "zarwana_recent",
    ids() { return store.get(this.KEY, []); },
    push(id) {
      const ids = [id, ...this.ids().filter((x) => x !== id)].slice(0, 4);
      store.set(this.KEY, ids);
    },
    clear() { store.set(this.KEY, []); },
  };
  window.ZCart = Cart; window.ZWish = Wish; window.ZRecent = Recent;

  /* ------------------------------ header --------------------------------- */
  const cat = (h) => `${URLS.catalog}?collection=${h}`;
  const caret = '<svg class="mega-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="m5 9 7 7 7-7"/></svg>';

  /* Featured + By Price columns are shared by every category dropdown so each
     panel stays full and consistent, even for categories with one collection. */
  const FEATURED_COL = { head: "Featured", links: [
    { t: "New Arrivals", href: `${URLS.catalog}?tag=new` },
    { t: "Bestsellers", href: `${URLS.catalog}?tag=bestseller` },
    { t: "Coins & Gifting", href: cat("coins") },
    { t: "Shop All Jewellery", href: URLS.catalog },
  ] };
  const PRICE_COL = { head: "By Price", links: [
    { t: "Under ₹2,000", href: `${URLS.catalog}?max=2000` },
    { t: "₹2,000 – ₹5,000", href: `${URLS.catalog}?min=2000&max=5000` },
    { t: "₹5,000 – ₹10,000", href: `${URLS.catalog}?min=5000&max=10000` },
    { t: "₹10,000 & Above", href: `${URLS.catalog}?min=10000` },
  ] };

  /* Top-level nav — category keys are the derived category keys (see
     products.js _deriveCategory); browsing is driven off productType. */
  const MENU = [
    { label: "Home", href: URLS.home, key: "home" },
    { label: "Catalog", href: URLS.catalog, key: "catalog",
      cols: [
        { head: "Shop by Category", links: [
          { t: "Earrings", href: cat("earrings") },
          { t: "Rings", href: cat("rings") },
          { t: "Anklets", href: cat("anklets") },
          { t: "Toe Rings", href: cat("toe-rings") },
          { t: "Chains", href: cat("chains") },
          { t: "Bangles", href: cat("bangles") },
          { t: "Bracelets", href: cat("bracelets") },
          { t: "Kada", href: cat("kada") },
        ] },
        { head: "Sets & Traditional", links: [
          { t: "Jewellery Sets", href: cat("sets") },
          { t: "Pendants", href: cat("pendants") },
          { t: "Mangalsutra", href: cat("mangalsutra") },
          { t: "Nazariya", href: cat("nazariya") },
        ] },
        { head: "More Pieces", links: [
          { t: "Silver Articles", href: cat("silver") },
          { t: "Coins", href: cat("coins") },
          { t: "Brooches", href: cat("brooches") },
        ] },
        FEATURED_COL, PRICE_COL,
      ],
      promo: [{ img: ASSETS.promo || "", t: "The Jewellery Sets Edit", href: cat("sets") }] },
    { label: "Our Story", href: URLS.about, key: "about" },
    { label: "Contact", href: URLS.contact, key: "contact" },
  ];

  const megaCols = (cols) => cols.map((c) => `
    <div class="mega-col">
      <h3 class="mega-head">${c.head}</h3>
      <ul>${c.links.map((l) => `<li><a href="${l.href}">${l.t}</a></li>`).join("")}</ul>
    </div>`).join("");
  const megaPromos = (promo) => !promo ? "" : `
    <div class="mega-promos">${promo.map((p) => `
      <a class="mega-promo${p.img ? "" : " mega-promo--noimg"}" href="${p.href}">
        ${p.img ? `<img src="${p.img}" alt="" loading="lazy" />` : ""}
        <span>${p.t}</span>
      </a>`).join("")}</div>`;

  const desktopNav = () => MENU.map((m) => {
    const active = page === m.key ? " active" : "";
    if (!m.cols) return `<li class="mega-item"><a class="mega-link${active}" href="${m.href}">${m.label}</a></li>`;
    return `
      <li class="mega-item has-mega">
        <a class="mega-link${active}" href="${m.href}" aria-haspopup="true" aria-expanded="false">${m.label}${caret}</a>
        <div class="mega-panel" role="region" aria-label="${m.label} menu">
          <div class="container mega-inner">
            <div class="mega-cols">${megaCols(m.cols)}</div>
            ${megaPromos(m.promo)}
          </div>
        </div>
      </li>`;
  }).join("");

  const drawerNav = () => MENU.map((m) => {
    if (!m.cols) return `<a class="drawer-link${page === m.key ? " active" : ""}" href="${m.href}">${m.label}</a>`;
    const subCols = m.cols.filter((c) => c !== FEATURED_COL && c !== PRICE_COL);
    const subs = subCols.flatMap((c) => c.links).map((l) => `<a href="${l.href}">${l.t}</a>`).join("");
    return `
      <div class="drawer-acc">
        <button class="drawer-acc-top" type="button" aria-expanded="false">${m.label}${caret}</button>
        <div class="drawer-acc-body">
          <a class="drawer-acc-all" href="${m.href}">Shop all ${m.href === URLS.catalog ? "jewellery" : m.label}</a>
          ${subs}
        </div>
      </div>`;
  }).join("");
  const SOCIALS = [
    { href: "https://www.instagram.com/zarwanaofficial/", label: "Instagram", icon: I.instagram },
    { href: "https://www.facebook.com/zarwanaofficial/", label: "Facebook", icon: I.facebook },
    { href: "https://www.youtube.com/@Zarwanaofficial", label: "YouTube", icon: I.youtube },
  ];
  const socialLinks = () => SOCIALS.map((s) => {
    const attrs = s.href === "#" ? 'data-ext' : 'target="_blank" rel="noopener"';
    return `<a class="social-icon" href="${s.href}" ${attrs} aria-label="${s.label}">${s.icon}</a>`;
  }).join("");

  /* site-wide sale bar, only while ZARWANA.SALE's window is open */
  const saleOn = typeof ZARWANA !== "undefined" && !!ZARWANA.saleLive && ZARWANA.saleLive();
  const saleBar = saleOn ? `
  <a class="sale-bar" href="${URLS.catalog}"><span class="sale-bar-label">${ZARWANA.SALE.label}</span><span class="sale-bar-dates">${ZARWANA.SALE.dates}</span></a>` : "";
  const headerHTML = `${saleBar}
  <div class="container header-bar">
    <div class="header-left">
      <button class="icon-btn burger" data-open-menu aria-label="Menu">${I.burger}</button>
      <div class="header-socials" role="group" aria-label="Social links">${socialLinks()}</div>
      <nav class="header-nav" aria-label="Primary"><ul class="mega-nav">${desktopNav()}</ul></nav>
    </div>
    <a class="header-logo" href="${URLS.home}" aria-label="Zarwana home">
      <span class="logo-mark">Zarwana</span>
      <span class="logo-sub">by Jodhamal Jewell Arts</span>
    </a>
    <div class="header-actions">
      <button class="icon-btn" data-open-search aria-label="Search">${I.search}</button>
      <a class="icon-btn" href="${URLS.account}" data-account-link aria-label="Account">${I.account}</a>
      <button class="icon-btn" data-open-cart aria-label="Cart">
        ${I.bag}<span class="cart-count" data-cart-count></span>
      </button>
      <a class="icon-btn" href="${URLS.catalog}?wishlist=1" aria-label="Wishlist">${I.heart}</a>
    </div>
  </div>`;

  const shellHTML = `
  <div class="menu-drawer" data-menu-drawer role="dialog" aria-modal="true" aria-label="Menu">
    <div class="drawer-top">
      <span class="logo-mark" style="font-family:var(--font-display);letter-spacing:.3em;font-size:18px;">ZARWANA</span>
      <button class="icon-btn" data-close-overlays aria-label="Close menu">${I.close}</button>
    </div>
    <nav class="drawer-nav">${drawerNav()}</nav>
    <div class="drawer-socials" role="group" aria-label="Social links">${socialLinks()}</div>
    <div class="drawer-foot">Premium 925 Sterling Silver Jewellery &middot; Jodhamal Jewell Arts</div>
  </div>

  <div class="overlay-backdrop" data-backdrop></div>
  <div class="mega-scrim" data-mega-scrim aria-hidden="true"></div>

  <aside class="cart-drawer" data-cart-drawer role="dialog" aria-modal="true" aria-label="Cart">
    <div class="drawer-head">
      <h3>Your Bag</h3>
      <button class="icon-btn" data-close-overlays aria-label="Close cart" style="color:var(--text-strong)">${I.close}</button>
    </div>
    <div class="drawer-body" data-cart-body></div>
    <div class="drawer-foot" data-cart-foot></div>
  </aside>

  <div class="search-modal" data-search-modal role="dialog" aria-label="Search">
    <div class="search-top">
      ${I.search}
      <input type="search" placeholder="Search" data-search-input aria-label="Search products" />
      <button class="icon-btn" data-close-overlays aria-label="Close search">${I.close}</button>
    </div>
    <div class="search-body" data-search-body></div>
  </div>

  <div class="quick-modal" data-quick-modal role="dialog" aria-label="Quick view">
    <button class="icon-btn quick-close" data-close-overlays aria-label="Close" style="color:var(--text-strong)">${I.close}</button>
    <div data-quick-body></div>
  </div>

  <a class="whatsapp-float" href="${WHATSAPP_URL}?text=${encodeURIComponent("Hi Zarwana, I need help with jewellery.")}"
     target="_blank" rel="noopener" data-whatsapp-float aria-label="Chat on WhatsApp">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
      <path d="M4.8 19.2 6 15.9a7.5 7.5 0 1 1 2.7 2.6l-3.9.7Z"/>
      <path d="M9.2 8.8c.2 3 2.1 4.8 5 5.7l1.3-1.3"/>
    </svg>
    <span>WhatsApp</span>
  </a>

  <div class="commerce-reminder" data-commerce-reminder aria-live="polite">
    <button class="commerce-reminder__close" type="button" data-commerce-reminder-close aria-label="Close reminder">${I.close}</button>
    <div>
      <strong data-commerce-reminder-title></strong>
      <p data-commerce-reminder-copy></p>
    </div>
    <div class="commerce-reminder__actions">
      <a class="commerce-reminder__primary" href="${URLS.cart}" data-commerce-reminder-primary></a>
      <a class="commerce-reminder__secondary" href="${WHATSAPP_URL}?text=${encodeURIComponent("Hi Zarwana, I would like help with my saved jewellery.")}" target="_blank" rel="noopener">Chat on WhatsApp</a>
    </div>
  </div>

  <div class="toast" data-toast></div>`;

  const footerHTML = `
  <div class="container footer-main">
    <div class="footer-brand">
      <img src="${ASSETS.logo || ""}" alt="Zarwana by Jodhamal Jewell Arts" />
      <p>Zarwana is a Jodhamal Jewell Arts brand — premium 925 sterling silver jewellery backed by the legacy of Jodhamal Kailash Chand Jain Jewellers.</p>
    </div>
    <div class="footer-col">
      <h4>About</h4>
      <a href="${URLS.about}">Our Story</a>
      <a href="https://www.instagram.com/zarwanaofficial/" target="_blank" rel="noopener">Instagram</a>
      <a href="https://www.facebook.com/zarwanaofficial/" target="_blank" rel="noopener">Facebook</a>
      <a href="${WHATSAPP_URL}" target="_blank" rel="noopener">WhatsApp</a>
    </div>
    <div class="footer-col">
      <h4>Shop</h4>
      <a href="${URLS.catalog}?tag=new">New Arrivals</a>
      <a href="${URLS.catalog}?tag=bestseller">Best Sellers</a>
      <a href="${URLS.catalog}">All Jewellery</a>
    </div>
    <div class="footer-col">
      <h4>Privacy &amp; Legal</h4>
      <a href="/pages/cancellation-policy">Cancellation Policy</a>
      <a href="/pages/return-policy">Return Policy</a>
      <a href="/pages/privacy-policy">Privacy Policy</a>
      <a href="/pages/terms">Terms &amp; Conditions</a>
    </div>
    <div class="footer-news">
      <iframe name="zarwana-newsletter-frame" style="display:none" aria-hidden="true" title="newsletter signup response"></iframe>
      <form class="news-form" data-news-form method="post" target="zarwana-newsletter-frame" action="/contact">
        <input type="hidden" name="form_type" value="customer" />
        <input type="hidden" name="utf8" value="&#10003;" />
        <input type="hidden" name="contact[tags]" value="newsletter" />
        <input type="email" name="contact[email]" placeholder="Email address" required aria-label="Email address" />
        <button class="btn" type="submit" style="letter-spacing:.06em;text-transform:none;font-size:13.5px;">Sign up</button>
      </form>
      <p class="news-ok" data-news-ok>Thank you — you're on the list.</p>
      <p class="small">You are signing up to receive Zarwana emails. You can withdraw your consent at any time.</p>
    </div>
  </div>
  <div class="footer-bottom">
    <div class="container">
      <span>&copy; 2026 Zarwana by Jodhamal Jewell Arts</span>
      <a href="#top">Back to top</a>
    </div>
  </div>`;

  /* ------------------------------ mount ---------------------------------- */
  const headerEl = $("[data-header]");
  if (headerEl) headerEl.innerHTML = headerHTML;
  if (headerEl && saleOn) document.body.classList.add("has-sale-bar");
  const footerEl = $("[data-footer]");
  if (footerEl) footerEl.innerHTML = footerHTML;
  document.body.insertAdjacentHTML("beforeend", shellHTML);

  /* header overlay behaviour (home hero) */
  if (headerEl && headerEl.classList.contains("header--overlay")) {
    const onScroll = () => headerEl.classList.toggle("scrolled", window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* --------------------- desktop mega-menu ------------------------------- */
  (function initMega() {
    if (!headerEl) return;
    const items = [...headerEl.querySelectorAll(".mega-item.has-mega")];
    if (!items.length) return;
    const scrim = $("[data-mega-scrim]");
    let openT, closeT, openItem = null;

    const reflect = (li, on) => {
      li.classList.toggle("open", on);
      const t = li.querySelector(".mega-link");
      if (t) t.setAttribute("aria-expanded", on ? "true" : "false");
    };
    const openMega = (li) => {
      clearTimeout(closeT);
      if (openItem && openItem !== li) reflect(openItem, false);
      reflect(li, true);
      openItem = li;
      headerEl.classList.add("mega-open");
      if (scrim) scrim.classList.add("show");
    };
    const closeMega = () => {
      clearTimeout(openT);
      if (openItem) reflect(openItem, false);
      openItem = null;
      headerEl.classList.remove("mega-open");
      if (scrim) scrim.classList.remove("show");
    };

    items.forEach((li) => {
      li.addEventListener("mouseenter", () => {
        clearTimeout(closeT); clearTimeout(openT);
        openT = setTimeout(() => openMega(li), 80);
      });
      li.addEventListener("mouseleave", () => {
        clearTimeout(openT);
        closeT = setTimeout(closeMega, 160);
      });
      li.addEventListener("focusin", () => openMega(li));
    });
    headerEl.addEventListener("focusout", (e) => {
      if (!headerEl.contains(e.relatedTarget)) closeMega();
    });
    headerEl.addEventListener("keydown", (e) => { if (e.key === "Escape") closeMega(); });
    if (scrim) scrim.addEventListener("mouseenter", closeMega);
    window.addEventListener("scroll", () => { if (openItem) closeMega(); }, { passive: true });
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".mega-item.has-mega")) closeMega();
    });
  })();

  /* --------------------- mobile drawer accordion ------------------------- */
  document.addEventListener("click", (e) => {
    const top = e.target.closest(".drawer-acc-top");
    if (!top) return;
    const acc = top.closest(".drawer-acc");
    const on = acc.classList.toggle("open");
    top.setAttribute("aria-expanded", on ? "true" : "false");
  });

  /* --------------------------- overlays ---------------------------------- */
  const backdrop = $("[data-backdrop]");
  const overlays = {
    menu: $("[data-menu-drawer]"),
    cart: $("[data-cart-drawer]"),
    search: $("[data-search-modal]"),
    quick: $("[data-quick-modal]"),
    filters: null, // registered by catalog page
  };
  /* A closed overlay stays in the DOM, off-screen but still tabbable, so a
     keyboard user used to fall into the closed cart drawer. `inert` is the
     right primitive: it removes an element from BOTH the tab order and the
     accessibility tree. (A bare aria-hidden cannot do the former, and
     aria-hidden over focusable children is itself a violation.)
     "filters" is deliberately excluded: above 1000px .plp-filters is not a
     drawer at all, it is an always-visible sidebar. */
  const INERT_WHEN_CLOSED = ["menu", "cart", "search", "quick"];
  const setInert = (el, on) => {
    if (!el) return;
    if (on) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  };
  INERT_WHEN_CLOSED.forEach((n) => setInert(overlays[n], true));

  let lastFocus = null;
  let suppressRestore = false;

  function openOverlay(name) {
    const trigger = document.activeElement;
    /* don't bounce focus back to the trigger on the way through */
    suppressRestore = true;
    closeOverlays();
    suppressRestore = false;

    const el = overlays[name];
    if (!el) return;
    lastFocus = trigger;
    setInert(el, false);
    el.classList.add("open");
    backdrop.classList.add("show");
    document.body.classList.add("no-scroll");
    if (name === "search") {
      setTimeout(() => $("[data-search-input]").focus(), 120);
    } else {
      const close = el.querySelector("[data-close-overlays]");
      if (close) setTimeout(() => close.focus(), 120);
    }
  }
  function closeOverlays() {
    Object.values(overlays).forEach((el) => el && el.classList.remove("open"));
    INERT_WHEN_CLOSED.forEach((n) => setInert(overlays[n], true));
    backdrop.classList.remove("show");
    document.body.classList.remove("no-scroll");
    /* return focus to whatever opened the overlay, so the keyboard user does
       not get dumped back at the top of the document */
    if (!suppressRestore && lastFocus && document.contains(lastFocus)) lastFocus.focus();
    if (!suppressRestore) lastFocus = null;
  }
  window.ZOverlay = { open: openOverlay, close: closeOverlays, register: (n, el) => (overlays[n] = el) };

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-open-menu]")) openOverlay("menu");
    if (e.target.closest("[data-open-cart]")) { renderCartUI(); openOverlay("cart"); }
    if (e.target.closest("[data-open-search]")) { renderSearch(""); openOverlay("search"); }
    if (e.target.closest("[data-close-overlays]")) closeOverlays();
    if (e.target === backdrop) closeOverlays();
    const ext = e.target.closest("[data-ext]");
    if (ext) { e.preventDefault(); toast("This social link will be updated soon"); }
  });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeOverlays(); });

  /* ------------------------------ toast ---------------------------------- */
  let toastTimer;
  function toast(msg) {
    const t = $("[data-toast]");
    t.textContent = msg;
    t.classList.add("show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove("show"), 2400);
  }
  window.ZToast = toast;

  /* --------------------------- product card ------------------------------
     Second-view hover: a card with a second angle uploaded changes its PHOTO
     under the cursor - never a zoom (Priyam: "the photo should change, it only
     zooms, we don't want that") - the way a counter assistant turns a piece in
     the light. A piece with one photo does nothing: it has nothing to turn to,
     and re-cropping its single shot is just the zoom under another name.
     Desktop only: without hover a second <img> is pure weight on the
     connection least able to afford it. */
  const FINE_POINTER =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  function cardHTML(p, opts = {}) {
    const boxed = opts.boxed !== false;
    const badge = p.soldOut
      ? '<span class="card-badge card-badge--soldout">Sold out</span>'
      : p.tag === "new" ? '<span class="card-badge card-badge--new">New</span>' : "";
    const wishOn = Wish.has(p.id);
    const quick = p.soldOut
      ? '<span class="quick-add" style="pointer-events:none;opacity:.75">Sold out</span>'
      : `<button class="quick-add" data-quick-add="${p.id}">${ZARWANA.hasOptions(p) ? "Choose" : "Add"}</button>`;
    /* featuredImage is not guaranteed to be images[0] - a merchant can promote
       any photo - so find the first one that genuinely differs, or the effect
       would cross-fade between two identical pictures. */
    const swap = FINE_POINTER ? ((p.images || []).find((u) => u && u !== p.img) || "") : "";
    return `
    <article class="product-card ${boxed ? "product-card--boxed" : "product-card--plain"}${swap ? " product-card--dual" : ""}">
      <div class="card-media">
        <a href="${URLS.product(p.id)}" aria-label="${p.name}">
          <img src="${ZARWANA.imgUrl(p.img, 700, 700)}" alt="${p.name}" loading="lazy" />
        </a>
        ${swap ? `<img class="card-img--2" src="${ZARWANA.imgUrl(swap, 700, 700)}" alt="" loading="lazy" decoding="async" fetchpriority="low" />` : ""}
        <button class="card-wish ${wishOn ? "active" : ""}" data-wish="${p.id}"
                aria-label="Add to wishlist" aria-pressed="${wishOn}">
          <span class="wish-off">${I.heart}</span><span class="wish-on">${I.heartFill}</span>
        </button>
        ${badge}
        ${quick}
      </div>
      <a class="card-info" href="${URLS.product(p.id)}">
        <div class="card-name">${p.name}</div>
        <div class="card-price">${ZARWANA.priceHTML(p)}</div>
      </a>
    </article>`;
  }
  window.ZCard = cardHTML;

  document.addEventListener("click", (e) => {
    const wishBtn = e.target.closest("[data-wish]");
    if (wishBtn) {
      e.preventDefault();
      const on = Wish.toggle(wishBtn.dataset.wish);
      wishBtn.classList.toggle("active", on);
      /* both glyphs are rendered; we toggle a class rather than replacing
         innerHTML, which is what used to kill the CSS transition */
      wishBtn.setAttribute("aria-pressed", on);
      toast(on ? "Saved to wishlist" : "Removed from wishlist");
      if (on) maybeShowCommerceReminder("wishlist", 900);
    }
    const qa = e.target.closest("[data-quick-add]");
    if (qa) {
      e.preventDefault();
      const p = ZARWANA.byId(qa.dataset.quickAdd);
      if (!p) return;
      /* rings need a size choice, so they always go through the quick view */
      if (ZARWANA.hasOptions(p) || ZARWANA.needsRingSize(p)) { openQuick(p); return; }
      const variant = ZARWANA.findVariant(p, p.sizes[0], p.purities[0]);
      if (!variant) { toast("That piece isn't available right now."); return; }
      Cart.add(variant.id, 1).then((ok) => { if (ok) { toast("Added to your bag"); openOverlay("cart"); } });
    }
  });

  /* --------------------------- quick view -------------------------------- */
  function openQuick(p) {
    const body = $("[data-quick-body]");
    body.innerHTML = `
      <div class="quick-grid">
        <div class="q-media"><img src="${ZARWANA.imgUrl(p.img, 800)}" alt="${p.name}" /></div>
        <div class="q-body">
          <h3>${p.name}</h3>
          <div class="q-price">${ZARWANA.priceHTML(p)}</div>
          <div class="select-wrap" hidden><select data-q-size>${p.sizes.map((s) => `<option>${s}</option>`).join("")}</select></div>
          ${ZARWANA.needsRingSize(p) ? `
          <label class="field-label">Ring Size</label>
          <div class="select-wrap"><select data-q-ringsize>
            <option value="">Select size</option>
            ${ZARWANA.RING_SIZES.map((s) => `<option>${s}</option>`).join("")}
          </select></div>` : ""}
          <label class="field-label">Metal / Purity</label>
          <div class="select-wrap"><select data-q-purity>${p.purities.map((s) => `<option>${s}</option>`).join("")}</select></div>
          <div style="margin-top:22px"><button class="btn btn--full" data-q-add="${p.id}">Add to bag</button></div>
          <a class="full-link" href="${URLS.product(p.id)}">View full details</a>
        </div>
      </div>`;
    openOverlay("quick");
    $("[data-q-add]", body).addEventListener("click", async () => {
      const variant = ZARWANA.findVariant(p, $("[data-q-size]", body).value, $("[data-q-purity]", body).value);
      if (!variant) { toast("That combination isn't available."); return; }
      let properties = null;
      if (ZARWANA.needsRingSize(p)) {
        const ringSize = $("[data-q-ringsize]", body).value;
        if (!ringSize) { toast("Please select a ring size."); return; }
        properties = { "Ring Size": ringSize };
      }
      const ok = await Cart.add(variant.id, 1, properties);
      if (ok) { toast("Added to your bag"); openOverlay("cart"); }
    });
  }
  window.ZQuick = openQuick;

  /* ---------------------------- cart drawer ------------------------------ */
  function renderCartUI() {
    /* count bubbles */
    $$("[data-cart-count]").forEach((el) => {
      const n = Cart.count();
      el.textContent = n;
      el.classList.toggle("show", n > 0);
    });
    const body = $("[data-cart-body]");
    const foot = $("[data-cart-foot]");
    if (!body) return;
    const items = Cart.items();
    if (!items.length) {
      body.innerHTML = `
        <div class="cart-empty">
          <h4>Your bag is empty</h4>
          <p>Have an account? <a class="login" href="${URLS.account}">Log in</a> to check out faster.</p>
          <a class="btn" href="${URLS.catalog}">Continue shopping</a>
        </div>`;
      foot.innerHTML = "";
      return;
    }
    body.innerHTML = items.map((l) => `
      <div class="cart-line">
        <a href="${URLS.product(l.productHandle)}"><img src="${ZARWANA.imgUrl(l.img, 300)}" alt="${l.name}" /></a>
        <div>
          <div class="line-name">${l.name}</div>
          ${l.variantTitle ? `<div class="line-variant">${l.variantTitle.replace(/ \/ /g, " &middot; ")}</div>` : ""}
          ${Object.entries(l.properties || {}).filter(([k, v]) => v && !k.startsWith("_"))
            .map(([k, v]) => `<div class="line-variant">${k}: ${v}</div>`).join("")}
          <div class="line-price">${ZARWANA.fmt(l.price)}</div>
          <button class="line-remove" data-line-remove="${l.lineId}">Remove</button>
        </div>
        <div class="qty-stepper">
          <button data-line-dec="${l.lineId}" data-line-qty="${l.qty}" aria-label="Decrease">&minus;</button>
          <span>${l.qty}</span>
          <button data-line-inc="${l.lineId}" data-line-qty="${l.qty}" aria-label="Increase">+</button>
        </div>
      </div>`).join("");
    foot.innerHTML = `
      <div class="cart-subtotal"><span>Subtotal</span><span>${ZARWANA.fmt(Cart.subtotal())}</span></div>
      <p class="cart-note">Taxes included. Shipping calculated at checkout.</p>
      <div style="display:grid;gap:8px">
        <button class="btn" data-go-checkout>Checkout</button>
        <a class="btn btn--light" style="border-color:var(--line)" href="${URLS.cart}">View bag</a>
      </div>`;
  }
  document.addEventListener("click", (e) => {
    const dec = e.target.closest("[data-line-dec]");
    const inc = e.target.closest("[data-line-inc]");
    const rem = e.target.closest("[data-line-remove]");
    if (dec) Cart.setQty(dec.dataset.lineDec, +dec.dataset.lineQty - 1);
    if (inc) Cart.setQty(inc.dataset.lineInc, +inc.dataset.lineQty + 1);
    if (rem) Cart.remove(rem.dataset.lineRemove);
  });
  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-go-checkout]")) return;
    if (Cart.count() > 0) location.href = Cart.checkoutUrl();
    else toast("Your bag is empty");
  });
  window.renderCartUI = renderCartUI;

  /* ------------------------------ search --------------------------------- */
  function searchItem(p) {
    return `
      <a class="search-item" href="${URLS.product(p.id)}">
        <img src="${ZARWANA.imgUrl(p.img, 400, 400)}" alt="${p.name}" loading="lazy" />
        <div class="s-name">${p.name}</div>
        <div class="s-price">${ZARWANA.priceHTML(p)}</div>
      </a>`;
  }
  function renderSearch(q) {
    const body = $("[data-search-body]");
    const query = q.trim().toLowerCase();
    let html = "";
    const recent = Recent.ids().map(ZARWANA.byId).filter(Boolean);
    if (!query && recent.length) {
      html += `<div class="search-label"><span>Recently viewed</span><button data-search-clear>Clear</button></div>
               <div class="search-grid">${recent.map(searchItem).join("")}</div>`;
    }
    const pool = query
      ? ZARWANA.searchProducts(query)
      : ZARWANA.products.slice(0, 4);
    html += `<div class="search-label"><span>Products</span><a href="${URLS.catalog}" style="text-decoration:underline;text-underline-offset:3px;font-size:12px">View all</a></div>`;
    html += pool.length
      ? `<div class="search-grid">${pool.slice(0, 8).map(searchItem).join("")}</div>`
      : `<p class="search-empty">No results for &ldquo;${q}&rdquo; — try &ldquo;rope&rdquo;, &ldquo;royale&rdquo; or browse the catalog.</p>`;
    body.innerHTML = html;
  }
  window.ZSearchRender = renderSearch;
  document.addEventListener("input", (e) => {
    if (e.target.matches("[data-search-input]")) renderSearch(e.target.value);
  });
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-search-clear]")) { Recent.clear(); renderSearch(""); }
  });

  /* --------------------------- newsletter -------------------------------- */
  document.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-news-form]");
    if (!form) return;
    const ok = form.parentElement.querySelector("[data-news-ok]");
    if (ok) ok.style.display = "none";
    const frame = document.querySelector(`iframe[name="${form.target}"]`);
    if (frame) {
      frame.addEventListener("load", () => {
        form.reset();
        form.style.display = "none";
        if (ok) ok.style.display = "block";
        toast("Thank you — you're on the list");
      }, { once: true });
    }
  });

  /* ---------------------- onsite commerce reminder ---------------------- */
  function hideCommerceReminder() {
    const panel = $("[data-commerce-reminder]");
    if (panel) panel.classList.remove("open");
  }
  function maybeShowCommerceReminder(kind, delay = 2800) {
    const panel = $("[data-commerce-reminder]");
    if (!panel || sessionStorage.getItem("zarwana_commerce_reminder_seen")) return;
    const hasCart = Cart.count() > 0;
    const hasWish = Wish.ids().length > 0;
    const mode = kind || (hasCart ? "cart" : hasWish ? "wishlist" : "");
    if (!mode) return;
    const title = $("[data-commerce-reminder-title]", panel);
    const copy = $("[data-commerce-reminder-copy]", panel);
    const primary = $("[data-commerce-reminder-primary]", panel);
    if (mode === "cart") {
      title.textContent = "Your bag is saved";
      copy.textContent = "Checkout securely whenever you're ready, or ask Zarwana on WhatsApp for help before ordering.";
      primary.textContent = "View bag";
      primary.href = URLS.cart;
    } else {
      title.textContent = "Your wishlist is saved";
      copy.textContent = "Need sizing, styling or a special offer? Send your saved jewellery to Zarwana support.";
      primary.textContent = "View wishlist";
      primary.href = `${URLS.catalog}?wishlist=1`;
    }
    sessionStorage.setItem("zarwana_commerce_reminder_seen", "1");
    setTimeout(() => panel.classList.add("open"), delay);
  }
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-commerce-reminder-close]")) hideCommerceReminder();
  });
  window.addEventListener("zarwana:cart", () => maybeShowCommerceReminder("cart"));

  /* ---------------------------- carousels -------------------------------- */
  window.ZCarousel = function initCarousel(wrap) {
    const track = $(".product-carousel", wrap);
    if (!track) return;
    const prev = document.createElement("button");
    prev.className = "carousel-btn prev"; prev.innerHTML = I.chevL; prev.setAttribute("aria-label", "Previous");
    const next = document.createElement("button");
    next.className = "carousel-btn next"; next.innerHTML = I.chevR; next.setAttribute("aria-label", "Next");
    wrap.append(prev, next);
    const step = () => track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 14 : 300;
    prev.addEventListener("click", () => track.scrollBy({ left: -step() * 2, behavior: "smooth" }));
    next.addEventListener("click", () => track.scrollBy({ left: step() * 2, behavior: "smooth" }));
    const sync = () => {
      prev.toggleAttribute("disabled", track.scrollLeft < 10);
      next.toggleAttribute("disabled", track.scrollLeft > track.scrollWidth - track.clientWidth - 10);
    };
    track.addEventListener("scroll", sync, { passive: true });
    setTimeout(sync, 100);
  };

  /* ------------------------------ reveal ---------------------------------
     IntersectionObserver is the primary trigger, but some automation/headless
     or heavily-throttled-background-tab contexts don't reliably deliver IO
     (or even rAF) callbacks. A setTimeout-driven bounding-rect fallback
     guarantees elements never get stuck at opacity:0 for a real visitor. */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); } });
  }, { threshold: 0.12 });
  let pending = [];
  let tickQueued = false;
  function checkPending() {
    tickQueued = false;
    const vh = window.innerHeight;
    pending = pending.filter((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0) { el.classList.add("in"); io.unobserve(el); return false; }
      return true;
    });
  }
  function queueCheck() { if (!tickQueued) { tickQueued = true; setTimeout(checkPending, 16); } }
  window.addEventListener("scroll", queueCheck, { passive: true });
  window.addEventListener("resize", queueCheck);
  window.ZReveal = () => {
    pending = $$(".reveal, .reveal-stagger");
    pending.forEach((el) => io.observe(el));
    queueCheck();
  };

  /* ------------------------------- boot ---------------------------------- */
  renderCartUI(); // instant paint of whatever's cached (empty on a fresh browser)
  Cart.ready = Cart.refresh(); // exposed so other page scripts (e.g. /cart) can await the first load
  Promise.all([ZARWANA.ready, Cart.ready]).then(() => maybeShowCommerceReminder());
  window.ZReveal();
})();
